/**
 * 电商套图 · 结果阶段：审核标记 + 按场景分组的结果网格 + 局部编辑 + 批量下载
 */
import { useState } from 'react';
import { Check, Download, Loader2, Maximize2, Pencil, RefreshCw, RotateCcw, Undo2, Wand2, X } from 'lucide-react';
import { suiteTheme } from '../theme';
import { resolveBrowserMediaUrl } from '../../../utils/storageUrl';
import type { SuiteReviewStatus, SuiteShot } from '../api';
import { MaskEditModal } from './MaskEditModal';

type ReviewFilter = 'all' | 'approved' | 'needs_redo';

function ReviewBadges({
  shot,
  onReview,
  reviewing,
}: {
  shot: SuiteShot;
  onReview: (shotId: string, reviewStatus: SuiteReviewStatus) => void;
  reviewing: boolean;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-xs transition disabled:opacity-50 ${
          shot.reviewStatus === 'approved'
            ? 'border-emerald-400/40 bg-emerald-400/10 text-emerald-600'
            : 'border-slate-200 text-zinc-500 hover:border-emerald-400/40 hover:text-emerald-600'
        }`}
        disabled={reviewing}
        onClick={() => onReview(shot.id, shot.reviewStatus === 'approved' ? 'pending' : 'approved')}
        title="标记通过"
      >
        {shot.reviewStatus === 'approved' ? <Check className="h-3 w-3" /> : <Check className="h-3 w-3" />}
        通过
      </button>
      <button
        type="button"
        className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-xs transition disabled:opacity-50 ${
          shot.reviewStatus === 'needs_redo'
            ? 'border-rose-400/40 bg-rose-400/10 text-rose-600'
            : 'border-slate-200 text-zinc-500 hover:border-rose-400/40 hover:text-rose-600'
        }`}
        disabled={reviewing}
        onClick={() => onReview(shot.id, shot.reviewStatus === 'needs_redo' ? 'pending' : 'needs_redo')}
        title="标记待重做"
      >
        {shot.reviewStatus === 'needs_redo' ? <Undo2 className="h-3 w-3" /> : <X className="h-3 w-3" />}
        待重做
      </button>
    </div>
  );
}

export function ResultsGrid({
  shots,
  downloading,
  onReroll,
  rerollingShotId,
  onPreview,
  onBatchDownload,
  onReview,
  reviewing,
  onEditSubmit,
  editing,
  onRegenerateAll,
  regenerating,
}: {
  shots: SuiteShot[];
  downloading: boolean;
  onReroll: (shotId: string) => void;
  rerollingShotId: string | null;
  onPreview: (image: string) => void;
  onBatchDownload: (shotIds: string[]) => void;
  onReview: (shotId: string, reviewStatus: SuiteReviewStatus) => void;
  reviewing: boolean;
  onEditSubmit: (shotId: string, payload: { instruction: string; maskImage: string }) => void;
  editing: boolean;
  onRegenerateAll: () => void;
  regenerating: boolean;
}) {
  const [selectedForZip, setSelectedForZip] = useState<string[]>([]);
  const [reviewFilter, setReviewFilter] = useState<ReviewFilter>('all');
  const [editingShot, setEditingShot] = useState<SuiteShot | null>(null);

  const successShots = shots.filter((shot) => shot.status === 'success' && shot.outputImage);
  const visibleShots = reviewFilter === 'all'
    ? shots
    : shots.filter((shot) => shot.status === 'success' && shot.reviewStatus === reviewFilter);
  const needsRedoIds = successShots.filter((shot) => shot.reviewStatus === 'needs_redo').map((shot) => shot.id);

  const toggle = (shotId: string) => {
    setSelectedForZip((prev) => (prev.includes(shotId) ? prev.filter((id) => id !== shotId) : [...prev, shotId]));
  };

  const idsToDownload = selectedForZip.length > 0 ? selectedForZip : successShots.map((shot) => shot.id);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex overflow-hidden rounded-xl border border-slate-200 bg-white">
          {(
            [
              { key: 'all', label: `全部（${shots.length}）` },
              { key: 'approved', label: `已通过（${successShots.filter((s) => s.reviewStatus === 'approved').length}）` },
              { key: 'needs_redo', label: `待重做（${needsRedoIds.length}）` },
            ] as const
          ).map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setReviewFilter(item.key)}
              className={`px-4 py-2 text-sm font-medium transition ${
                reviewFilter === item.key ? 'bg-blue-100 text-blue-600' : 'text-zinc-500 hover:text-zinc-700'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            className={suiteTheme.ghostBtn}
            onClick={onRegenerateAll}
            disabled={regenerating || shots.length === 0}
            title="按风格锁对全部分镜重新生成（审核状态重置）"
          >
            {regenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            风格锁重生成全组
          </button>
          <button
            type="button"
            className={suiteTheme.ghostBtn}
            onClick={() => needsRedoIds.forEach((id) => onReroll(id))}
            disabled={needsRedoIds.length === 0 || rerollingShotId !== null}
          >
            <RotateCcw className="h-4 w-4" />
            重做全部待重做（{needsRedoIds.length}）
          </button>
          <button
            type="button"
            className={suiteTheme.primaryBtn}
            onClick={() => onBatchDownload(idsToDownload)}
            disabled={downloading || successShots.length === 0}
          >
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            打包下载{selectedForZip.length > 0 ? `（${selectedForZip.length}）` : ''}
          </button>
        </div>
      </div>

      {shots.length === 0 ? (
        <div className={`${suiteTheme.card} px-6 py-16 text-center text-sm text-zinc-500`}>
          暂无分镜结果，请先在「分镜」阶段完成规划与生成
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2 xl:grid-cols-3">
          {visibleShots.map((shot) => (
            <div key={shot.id} className="flex flex-col gap-3">
              <div className="flex items-center justify-between gap-2">
                <h4 className="truncate text-lg font-semibold text-zinc-800" title={shot.displayName}>
                  {shot.displayName}
                </h4>
                <span className="shrink-0 text-xs text-zinc-500">1 张</span>
              </div>

              <div className={`group relative aspect-square overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 ${suiteTheme.cardHover}`}>
                {shot.status === 'success' && shot.outputImage ? (
                  <>
                    <button type="button" className="block h-full w-full" onClick={() => shot.outputImage && onPreview(shot.outputImage)}>
                      <img src={resolveBrowserMediaUrl(shot.outputImage)} alt={shot.displayName} className="h-full w-full object-cover" />
                    </button>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => toggle(shot.id)}
                      onKeyDown={(e) => e.key === 'Enter' && toggle(shot.id)}
                      className={`absolute left-3 top-3 flex h-5 w-5 cursor-pointer items-center justify-center rounded-md border text-xs transition ${
                        selectedForZip.includes(shot.id)
                          ? 'border-blue-200 bg-blue-400 text-zinc-950'
                          : 'border-white/30 bg-zinc-950/40 opacity-0 group-hover:opacity-100'
                      }`}
                    >
                      {selectedForZip.includes(shot.id) && '✓'}
                    </span>
                    {shot.reviewStatus === 'approved' && (
                      <span className={suiteTheme.badgeSuccess}>已通过</span>
                    )}
                  </>
                ) : shot.status === 'generating' ? (
                  <div className="flex h-full flex-col items-center justify-center gap-2 text-zinc-500">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span className="text-xs">生成中…</span>
                  </div>
                ) : shot.status === 'failed' ? (
                  <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
                    <span className="text-xs text-rose-600">{shot.errorMessage || '生成失败'}</span>
                  </div>
                ) : (
                  <div className="flex h-full items-center justify-center text-xs text-zinc-600">尚未生成</div>
                )}

                <div className="absolute right-3 top-3 flex gap-1.5 opacity-0 transition group-hover:opacity-100">
                  {shot.outputImage && (
                    <button
                      type="button"
                      onClick={() => shot.outputImage && onPreview(shot.outputImage)}
                      className="rounded-lg bg-zinc-950/70 p-1.5 text-zinc-600 transition hover:text-blue-600"
                      aria-label="放大预览"
                    >
                      <Maximize2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {shot.status === 'success' && shot.outputImage && (
                    <button
                      type="button"
                      onClick={() => setEditingShot(shot)}
                      className="rounded-lg bg-zinc-950/70 p-1.5 text-zinc-600 transition hover:text-blue-600"
                      aria-label="局部编辑"
                      title="蒙版局部编辑"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs text-zinc-500">
                  {shot.protectionMode === 'PIXEL_PROTECTED' ? '像素保护' : '创意'}
                  {shot.generation > 1 ? ` · 第 ${shot.generation} 版` : ''}
                </span>
                <div className="flex items-center gap-1.5">
                  {shot.status === 'success' && (
                    <ReviewBadges shot={shot} onReview={onReview} reviewing={reviewing} />
                  )}
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-xs text-zinc-600 transition hover:border-blue-300 hover:text-blue-600 disabled:opacity-50"
                    disabled={rerollingShotId === shot.id}
                    onClick={() => onReroll(shot.id)}
                  >
                    {rerollingShotId === shot.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3 w-3" />
                    )}
                    重新生成
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editingShot && editingShot.outputImage && (
        <MaskEditModal
          image={editingShot.outputImage}
          displayName={editingShot.displayName}
          submitting={editing}
          onClose={() => setEditingShot(null)}
          onSubmit={(payload) => {
            onEditSubmit(editingShot.id, payload);
            setEditingShot(null);
          }}
        />
      )}
    </div>
  );
}
